import { makeEmptyScreenNavigate } from '../../../../shared/app/navigate';
import { mpsPostMessage } from '../../../../shared/app/util';
import { dataStr } from '../../../../shared/util/dataStr';
import { preventEscape } from '../../../../shared/util/preventEscape';
import { getSharedData } from '../../../../shared/util/rune';
import { makePlusUrl } from '../../../../shared/util/url';
import { Confirm } from '../Confirm/Confirm';
import klass from './CreatorCard.module.scss';
import { CustomEventWithDetail, html, on, View, type Html } from 'rune-ts';
import { htmlIf, isAllCrewById } from '../../../../shared/util';
import { HeartFillIcon, MShopThinIcon } from '../../atoms/Icon';
import { UtilS } from '../../../../../../modules/Util/S/Function/module/UtilS';
import { staticTypo, typo } from '../../../../shared/typography/typo';
import $dataStr from 'fxdom/es/dataStr.js';
import { LegacyProductThumbnail } from '../ProductThumbnail/LegacyProductThumbnail.ts';
import { ProductThumbnailOption } from '../../../../features/ProductList/type';
import { CreatorTypeS } from '../../../../features/Creator/outbound/share';
import axios from 'axios';
import { ConfigSentryF } from '../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { MShopUtilF } from '../../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { getResizedUrl } from '../../../../shared/util/image';
import { AdultVerificationIcon } from '../ProductThumbnail/AdultVerificationIcon';
import { CreatorThumbnail } from '../CreatorThumbnail/CreatorThumbnail';
import { AdultVerificationThumbnail } from '../ProductThumbnail/AdultVerificationThumbnail';

interface PostMessage {
  screen: { type: string; uri: string };
}

type CreatorCardProps = CreatorTypeS.CreatorCardType & {
  adult_check_needed?: boolean;
};

export class CreatorLikeToggleEvent extends CustomEventWithDetail<{
  toggleLikeButton: () => void;
}> {}

export class CreatorCard extends View<CreatorCardProps> {
  protected override template() {
    const { name, thumbnail_img, domain_name, main_store_logo, seller_type, like_count, is_liked, products } =
      this.data;

    const sharedData = getSharedData(this);

    const main_store_logo_img = () => {
      if (main_store_logo) {
        return html`<img src="${main_store_logo}" class="${klass.main_store_logo}" alt="" />`;
      } else {
        return '';
      }
    };

    return html`
      <div
        class="${klass.CreatorCard}"
        style="background-image: url('${getResizedUrl({
          url: thumbnail_img || '',
          width: 374 * 2,
          format: 'webp',
          quality: 70,
        })}')"
      >
        <div class="${klass.bg}"></div>
        <div class="${klass.wrap}">
          <div class="${klass.thumb}">
            <a
              href="/${ET.lang}/${domain_name}"
              data-post-message="${dataStr(
                makeEmptyScreenNavigate(
                  sharedData?.plus_crew && isAllCrewById(sharedData.plus_crew.id)
                    ? `/${ET.lang}/${domain_name}`
                    : makePlusUrl(sharedData?.plus_crew?.domain_name, `/${ET.lang}/${domain_name}`),
                ),
              )}"
            >
              ${this.selectThumbnail(
                new CreatorThumbnail(
                  { ...this.data, resize_width: 374 * 2 },
                  { klass: klass.thumb_img, adult_check_needed: this.data.adult_check_needed },
                ),
                new AdultVerificationIcon({ klass: klass.adult_thumb }),
              )}
              ${main_store_logo_img()}
            </a>
          </div>
          <div class="${klass.txt_info}">
            <div class="${klass.name} ${typo('16_bold')}">
              <a
                href="/${ET.lang}/${domain_name}"
                data-post-message="${dataStr(makeEmptyScreenNavigate(`/${ET.lang}/${domain_name}`))}"
              >
                <span class="${klass.store_name}">${html.preventEscape(name ?? '')}</span>
                <span class="${klass.mshop_thin_icon}">${MShopThinIcon}</span>
              </a>
            </div>
            <div class="${klass.seller_type} ${typo('14_medium')}">${seller_type || ' '}</div>
          </div>

          <div class="${klass.products}">
            ${products.map((product) => {
              const option: ProductThumbnailOption = { resize_width: 300, img_alt: product.name || '' };
              const post_message = this.makePostMessage(product);
              const thumbnailHtml = html`
                <div class="${klass.product_thumb}">
                  <a href="${post_message.screen.uri}" data-post-message="${$dataStr(post_message)}"
                    >${new LegacyProductThumbnail(product, option)}</a
                  >
                </div>
              `;

              return this.selectThumbnail(
                thumbnailHtml,
                new AdultVerificationThumbnail({ klass: klass.product_adult_thumb }),
              );
            })}
          </div>
          <div class="${klass.like_area}">
            <button type="button" class="${klass.like_btn} ${htmlIf(klass.active, is_liked)}">
              <span class="${klass.like}">${HeartFillIcon}</span>
              <span
                class="${klass.like_count} ${staticTypo('unica_14_regular')} ${htmlIf(
                  klass.active,
                  is_liked,
                )}"
                >${UtilS.commify(like_count)}</span
              >
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private selectThumbnail(thumbnail: Html | View, adult_thumbnail: Html | View) {
    // 1. 성인인증 필요 없는 크리에이터
    if (!this.data.is_adult) {
      return thumbnail;
    }

    // 2. 성인인증 필요한 크리에이터인데, 성인인증 안 한 경우
    if (this.data.is_adult && this.data.adult_check_needed) {
      return adult_thumbnail;
    }

    // 3. 성인인증 필요한 크리에이터인데, 성인인증 한 경우
    return thumbnail;
  }

  private makePostMessage(product: CreatorTypeS.CreatorCardProduct): PostMessage {
    return {
      screen: {
        type: 'util',
        uri: `/${T.lang}/${this.data.domain_name}/products/${product.id}`,
      },
    };
  }

  @on('click', `.${klass.like_btn}`)
  private _onLikeClick() {
    this.dispatchEvent(CreatorLikeToggleEvent, {
      bubbles: true,
      detail: {
        toggleLikeButton: this.toggleLikeButton.bind(this),
      },
    });
  }

  toggleLikeButtonStyle() {
    this.data.like_count = this.data.is_liked ? this.data.like_count - 1 : this.data.like_count + 1;
    this.data.is_liked = !this.data.is_liked;
    this.redraw();
  }

  async toggleLikeButton() {
    if (!box.sel('is_user->id') || box.sel('is_user->type') == 'TEMP') {
      const confirmed = (await Confirm.open({
        title: ET('mps2::login::need_login::title'),
        message: preventEscape(ET('mps2::login::need_login::message')),
        confirmText: ET('mps2::login::login'),
        cancelText: ET('mps2::signup::close'),
      })) as boolean;

      if (confirmed) {
        if (MShopUtilF.isApp()) {
          mpsPostMessage(
            makeEmptyScreenNavigate(`/${T.lang}/@/login?url=${location.pathname + location.search}`),
          );
        } else {
          location.href = `/${T.lang}/@/login?url=${location.pathname + location.search}`;
        }
      }
      return;
    }

    const already_like = !!this.data.is_liked;

    this.toggleLikeButtonStyle();

    try {
      await axios.post(UtilS.makeApiUrl(`/:lang/@api/stores_like`, { lang: T.lang }), {
        already_like,
        store_id: this.data.id,
      });
      MShopUtilF.popToastMsg(
        already_like
          ? T('mshop::좋아요 목록에서 삭제되었습니다.')
          : T('mshop::좋아요 목록에 추가되었습니다.'),
        already_like ? 'error' : 'store_like',
      );
    } catch (err) {
      this.toggleLikeButtonStyle();
      ConfigSentryF.error(err);
    }
  }
}
